import { fonts } from '@/constants/fonts';
import { useThemeColor } from '@/hooks/useThemeColor';
import { Ionicons } from '@expo/vector-icons';
import React, { ReactNode, useState } from 'react';
import {
  FlatList,
  Modal,
  Pressable,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  View,
  type ViewStyle,
} from 'react-native';
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import ThemedText from '../core/ThemedText';

export interface SelectOption {
  label: string;
  value: string;
  icon?: ReactNode;
  disabled?: boolean;
}

interface SelectProps {
  options: SelectOption[];
  value?: string;
  onValueChange: (value: string) => void;
  placeholder?: string;
  label?: string;
  error?: string;
  disabled?: boolean;
  searchable?: boolean;
  multiple?: boolean;
  style?: ViewStyle;
}

export default function Select({
  options,
  value,
  onValueChange,
  placeholder = 'Select an option',
  label,
  error,
  disabled = false,
  searchable = false,
  multiple = false,
  style,
}: SelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const backgroundColor = useThemeColor({}, 'background');
  const backgroundSecondary = useThemeColor({}, 'backgroundSecondary');
  const textColor = useThemeColor({}, 'text');
  const textSecondary = useThemeColor({}, 'textSecondary');
  const borderColor = useThemeColor({}, 'border');
  const primaryColor = useThemeColor({}, 'primary');
  const errorColor = useThemeColor({}, 'error');

  const rotation = useSharedValue(0);

  const selectedOption = options.find((opt) => opt.value === value);

  const filteredOptions = searchable
    ? options.filter((opt) =>
        opt.label.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : options;

  const handleOpen = () => {
    if (disabled) return;
    setIsOpen(true);
    rotation.value = withTiming(180, { duration: 200 });
  };

  const handleClose = () => {
    setIsOpen(false);
    setSearchQuery('');
    rotation.value = withTiming(0, { duration: 200 });
  };

  const handleSelect = (optionValue: string) => {
    onValueChange(optionValue);
    handleClose();
  };

  const chevronStyle = useAnimatedStyle(() => ({
    transform: [{ rotate: `${rotation.value}deg` }],
  }));

  return (
    <View style={[styles.container, style]}>
      {/* Label */}
      {label && (
        <ThemedText style={[styles.label, { color: textColor }]}>
          {label}
        </ThemedText>
      )}

      {/* Select Trigger */}
      <TouchableOpacity
        style={[
          styles.trigger,
          {
            backgroundColor: backgroundSecondary,
            borderColor: error ? errorColor : borderColor,
          },
          disabled && styles.disabled,
        ]}
        onPress={handleOpen}
        disabled={disabled}
        activeOpacity={0.7}
      >
        <View style={styles.triggerContent}>
          {selectedOption?.icon && (
            <View style={styles.selectedIcon}>{selectedOption.icon}</View>
          )}
          <ThemedText
            style={[
              styles.triggerText,
              !selectedOption && { color: textSecondary },
            ]}
            numberOfLines={1}
          >
            {selectedOption?.label || placeholder}
          </ThemedText>
        </View>
        <Animated.View style={chevronStyle}>
          <Ionicons name="chevron-down" size={20} color={textColor} />
        </Animated.View>
      </TouchableOpacity>

      {/* Error Message */}
      {error && (
        <ThemedText style={[styles.error, { color: errorColor }]}>
          {error}
        </ThemedText>
      )}

      {/* Dropdown Modal */}
      <Modal
        visible={isOpen}
        transparent
        animationType="fade"
        onRequestClose={handleClose}
      >
        <Pressable style={styles.overlay} onPress={handleClose}>
          <View
            style={[styles.dropdown, { backgroundColor: backgroundSecondary }]}
            onStartShouldSetResponder={() => true}
          >
            {/* Search Input */}
            {searchable && (
              <View
                style={[
                  styles.searchContainer,
                  { borderBottomColor: borderColor },
                ]}
              >
                <Ionicons
                  name="search"
                  size={20}
                  color={textSecondary}
                  style={styles.searchIcon}
                />
                <TextInput
                  style={[styles.searchInput, { color: textColor }]}
                  placeholder="Search..."
                  placeholderTextColor={textSecondary}
                  value={searchQuery}
                  onChangeText={setSearchQuery}
                  autoFocus
                />
              </View>
            )}

            {/* Options List */}
            <FlatList
              data={filteredOptions}
              keyExtractor={(item) => item.value}
              renderItem={({ item }) => (
                <TouchableOpacity
                  style={[
                    styles.option,
                    item.value === value && {
                      backgroundColor: primaryColor + '15',
                    },
                    item.disabled && styles.optionDisabled,
                  ]}
                  onPress={() => handleSelect(item.value)}
                  disabled={item.disabled}
                  activeOpacity={0.7}
                >
                  {item.icon && (
                    <View style={styles.optionIcon}>{item.icon}</View>
                  )}
                  <ThemedText
                    style={[
                      styles.optionText,
                      item.value === value && {
                        color: primaryColor,
                        fontFamily: fonts.semiBold,
                      },
                      item.disabled && { color: textSecondary },
                    ]}
                  >
                    {item.label}
                  </ThemedText>
                  {item.value === value && (
                    <Ionicons
                      name="checkmark"
                      size={20}
                      color={primaryColor}
                      style={styles.checkmark}
                    />
                  )}
                </TouchableOpacity>
              )}
              ListEmptyComponent={
                <View style={styles.emptyContainer}>
                  <ThemedText style={{ color: textSecondary }}>
                    No options found
                  </ThemedText>
                </View>
              }
              style={styles.optionsList}
              showsVerticalScrollIndicator={false}
            />
          </View>
        </Pressable>
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    marginBottom: 16,
  },
  label: {
    fontSize: 14,
    fontFamily: fonts.medium,
    marginBottom: 8,
  },
  trigger: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: 48,
    paddingHorizontal: 16,
    borderRadius: 12,
    borderWidth: 1,
  },
  triggerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  selectedIcon: {
    marginRight: 8,
  },
  triggerText: {
    fontSize: 16,
    fontFamily: fonts.regular,
    flex: 1,
  },
  disabled: {
    opacity: 0.5,
  },
  error: {
    fontSize: 12,
    fontFamily: fonts.regular,
    marginTop: 4,
  },
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  dropdown: {
    width: '100%',
    maxHeight: '70%',
    borderRadius: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 12,
    elevation: 8,
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    borderBottomWidth: 1,
  },
  searchIcon: {
    marginRight: 8,
  },
  searchInput: {
    flex: 1,
    fontSize: 16,
    fontFamily: fonts.regular,
    padding: 0,
  },
  optionsList: {
    maxHeight: 400,
  },
  option: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    minHeight: 48,
  },
  optionIcon: {
    marginRight: 12,
  },
  optionText: {
    fontSize: 16,
    fontFamily: fonts.regular,
    flex: 1,
  },
  checkmark: {
    marginLeft: 8,
  },
  optionDisabled: {
    opacity: 0.5,
  },
  emptyContainer: {
    padding: 32,
    alignItems: 'center',
  },
});
